import React, { useState, useEffect, useRef } from 'react';
import { CatalogItem } from '../types';
import {
  getAllCatalogItems,
  createCatalogItem,
  updateCatalogItem,
  deleteCatalogItem,
  uploadCatalogImage
} from '../services/catalogService';
import { v4 as uuidv4 } from 'uuid';
import {
  AdminContainer,
  AdminHeader,
  AdminTitle,
  AdminButton,
  ProjectsGrid,
  ProjectCard,
  ProjectImageContainer,
  ProjectImage,
  ProjectInfo,
  ProjectTitle,
  ProjectLocation,
  ProjectActions,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  FormCheckbox,
  FileUploadContainer,
  ImagePreviewContainer,
  ImagePreview,
  ErrorMessage,
  SuccessMessage
} from '../styles/AdminStyles';

const itemTypes = [
  'mobilier',
  'objet',
  'art-de-table',
  'all'
];

const CatalogAdmin: React.FC = () => {
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<CatalogItem | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  // Formulaire contrôlé
  const [formData, setFormData] = useState<Omit<CatalogItem, 'id' | 'createdAt'>>({
    title: '',
    description: '',
    image: '',
    type: 'mobilier',
    designer: '',
    material: '',
    dimensions: '',
    year: new Date().getFullYear(),
    isPublished: true
  });
  
  // Référence vers l'input de fichier
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  // Chargement initial des éléments du catalogue
  useEffect(() => {
    loadCatalogItems();
  }, []);
  
  // Chargement des éléments du catalogue
  const loadCatalogItems = async () => {
    try {
      const data = await getAllCatalogItems();
      setCatalogItems(data);
    } catch (err) {
      console.error('Erreur lors du chargement des éléments du catalogue:', err);
      setError('Impossible de charger les éléments du catalogue. Veuillez réessayer.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };
  
  const openNewItemModal = () => {
    setActiveItem(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      type: 'mobilier',
      designer: '',
      material: '',
      dimensions: '',
      year: new Date().getFullYear(),
      isPublished: true
    });
    setImageFile(null);
    setIsModalOpen(true);
  };
  
  const openEditItemModal = (item: CatalogItem) => {
    setActiveItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      image: item.image,
      type: item.type,
      designer: item.designer || '',
      material: item.material || '',
      dimensions: item.dimensions || '',
      year: item.year || new Date().getFullYear(),
      isPublished: item.isPublished !== undefined ? item.isPublished : true
    });
    setImageFile(null);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveItem(null);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsUploading(true);
      setError(null);
      
      // Création ou mise à jour de l'élément du catalogue
      if (activeItem) {
        // Mise à jour d'un élément existant
        const itemId = activeItem.id as string;
        
        // Upload de l'image si elle a été modifiée
        if (imageFile) {
          const imageUrl = await uploadCatalogImage(imageFile, itemId, formData.title);
          formData.image = imageUrl;
        }
        
        // Mettre à jour l'élément
        await updateCatalogItem(itemId, formData);
      } else {
        // Création d'un nouvel élément
        const newItemId = uuidv4();
        
        // Upload de l'image
        let imageUrl = '';
        if (imageFile) {
          imageUrl = await uploadCatalogImage(imageFile, newItemId, formData.title);
        } else {
          setError("Veuillez sélectionner une image pour l'élément du catalogue");
          setIsUploading(false);
          return;
        }
        
        // Créer l'élément
        await createCatalogItem({
          ...formData,
          image: imageUrl,
        });
      }
      
      await loadCatalogItems();
      setSuccess('Élément du catalogue mis à jour avec succès');
      setTimeout(() => setSuccess(null), 3000);
      closeModal();
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDeleteItem = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément du catalogue ?')) {
      try {
        await deleteCatalogItem(id);
        await loadCatalogItems();
        setSuccess('Élément du catalogue supprimé avec succès');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Erreur lors de la suppression de l\'élément du catalogue');
        console.error(err);
      }
    }
  };

  return (
    <AdminContainer>
      <AdminHeader>
        <AdminTitle>Gestion du Catalogue</AdminTitle>
        <AdminButton onClick={openNewItemModal}>Ajouter un élément</AdminButton>
      </AdminHeader>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      
      {catalogItems.length === 0 ? (
        <p>Aucun élément trouvé dans le catalogue.</p>
      ) : (
        <ProjectsGrid>
          {catalogItems.map(item => (
            <ProjectCard key={item.id}>
              <ProjectImageContainer>
                <ProjectImage 
                  src={item.image || '/images/placeholder.jpg'} 
                  alt={item.title} 
                />
              </ProjectImageContainer>
              <ProjectInfo>
                <ProjectTitle>{item.title}</ProjectTitle>
                <ProjectLocation>{item.type} {item.year && `- ${item.year}`}</ProjectLocation>
                <div>{item.isPublished ? 'Publié' : 'Brouillon'}</div>
                <ProjectActions>
                  <AdminButton onClick={() => openEditItemModal(item)}>
                    Modifier
                  </AdminButton>
                  <AdminButton onClick={() => handleDeleteItem(item.id!)}>
                    Supprimer
                  </AdminButton>
                </ProjectActions>
              </ProjectInfo>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      )}
      
      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <ModalCloseButton onClick={closeModal}>&times;</ModalCloseButton>
            <h2>{activeItem ? 'Modifier l\'élément' : 'Ajouter un élément au catalogue'}</h2>
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="title">Titre*</FormLabel>
                <FormInput
                  id="title"
                  name="title"
                  value={formData.title || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="description">Description</FormLabel>
                <FormTextarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="type">Type*</FormLabel>
                <FormSelect
                  id="type"
                  name="type"
                  value={formData.type || 'mobilier'}
                  onChange={handleInputChange}
                  required
                >
                  {itemTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="designer">Designer</FormLabel>
                <FormInput
                  id="designer"
                  name="designer"
                  value={formData.designer || ''}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="material">Matériaux</FormLabel>
                <FormInput
                  id="material"
                  name="material"
                  value={formData.material || ''}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="dimensions">Dimensions</FormLabel>
                <FormInput
                  id="dimensions"
                  name="dimensions"
                  value={formData.dimensions || ''}
                  onChange={handleInputChange}
                  placeholder="Ex: 120x80x75 cm"
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="year">Année</FormLabel>
                <FormInput
                  id="year"
                  name="year"
                  type="number"
                  value={formData.year || new Date().getFullYear()}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Image*</FormLabel>
                {formData.image && !imageFile && (
                  <ImagePreviewContainer>
                    <ImagePreview>
                      <img src={formData.image} alt="Aperçu" />
                    </ImagePreview>
                  </ImagePreviewContainer>
                )}
                {imageFile && (
                  <ImagePreviewContainer>
                    <ImagePreview>
                      <img 
                        src={URL.createObjectURL(imageFile)} 
                        alt="Aperçu" 
                      />
                      <button 
                        type="button" 
                        onClick={() => setImageFile(null)}
                      >
                        ✕
                      </button>
                    </ImagePreview>
                  </ImagePreviewContainer>
                )}
                <FileUploadContainer onClick={() => imageInputRef.current?.click()}>
                  <p>Cliquez ou glissez-déposez pour ajouter une image</p>
                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </FileUploadContainer>
              </FormGroup>
              
              <FormGroup>
                <FormCheckbox>
                  <input
                    type="checkbox"
                    id="isPublished"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="isPublished">Publier l'élément</label>
                </FormCheckbox>
              </FormGroup>
              
              <ModalFooter>
                <AdminButton type="submit" disabled={isUploading}>
                  {isUploading ? 'Chargement...' : (activeItem ? 'Mettre à jour' : 'Ajouter')}
                </AdminButton>
                <AdminButton type="button" onClick={closeModal}>
                  Annuler
                </AdminButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminContainer>
  );
};

export default CatalogAdmin;
